import type { ActionCodeSettings } from 'firebase/auth';

export const AUTH_ACTION_PATH = '/auth/action';
export const PRODUCTION_AUTH_ACTION_ORIGIN = 'https://app.vectormethods.com';

export type FirebaseAuthActionMode = 'verifyEmail' | 'resetPassword' | 'unsupported';

export interface ParsedFirebaseAuthAction {
  active: boolean;
  mode: FirebaseAuthActionMode;
  oobCode: string | null;
}

type LocationLike = Pick<Location, 'hostname' | 'origin'>;

const LOCALHOST_HOSTNAMES = new Set(['localhost', '127.0.0.1', '::1']);

function normalizeHostname(hostname: string): string {
  return hostname.trim().replace(/^\[(.*)\]$/, '$1');
}

function getCurrentLocation(): LocationLike {
  if (typeof window === 'undefined') {
    return { hostname: '', origin: PRODUCTION_AUTH_ACTION_ORIGIN };
  }
  return window.location;
}

function normalizeOrigin(origin: string): string {
  const trimmedOrigin = origin.trim().replace(/\/+$/, '');
  try {
    return new URL(trimmedOrigin).origin;
  } catch {
    return trimmedOrigin;
  }
}

export function resolveAuthActionOrigin(locationLike?: LocationLike): string {
  const configuredOrigin = process.env.REACT_APP_AUTH_ACTION_ORIGIN?.trim();
  if (configuredOrigin && configuredOrigin !== 'undefined' && configuredOrigin !== 'null') {
    return normalizeOrigin(configuredOrigin);
  }

  const resolvedLocation = locationLike ?? getCurrentLocation();
  if (LOCALHOST_HOSTNAMES.has(normalizeHostname(resolvedLocation.hostname))) {
    return normalizeOrigin(resolvedLocation.origin);
  }

  return PRODUCTION_AUTH_ACTION_ORIGIN;
}

export function buildAuthActionUrl(locationLike?: LocationLike): string {
  return `${resolveAuthActionOrigin(locationLike)}${AUTH_ACTION_PATH}`;
}

export function buildAuthActionCodeSettings(locationLike?: LocationLike): ActionCodeSettings {
  return {
    url: buildAuthActionUrl(locationLike),
    handleCodeInApp: true,
  };
}

export function isAuthActionPath(pathname: string): boolean {
  const normalizedPath = pathname.replace(/\/+$/, '') || '/';
  return normalizedPath === AUTH_ACTION_PATH;
}

export function parseFirebaseAuthAction(search: string, pathname = ''): ParsedFirebaseAuthAction {
  const params = new URLSearchParams(search || '');
  const modeParam = params.get('mode');
  const legacyAuthAction = params.get('authAction');
  const oobCode = params.get('oobCode');
  const onAuthActionPath = isAuthActionPath(pathname);
  const hasActionParams = Boolean(modeParam || legacyAuthAction || oobCode || params.get('apiKey'));

  let mode: FirebaseAuthActionMode = 'unsupported';
  if (modeParam === 'verifyEmail' || legacyAuthAction === 'verifyEmail') {
    mode = 'verifyEmail';
  } else if (modeParam === 'resetPassword') {
    mode = 'resetPassword';
  }

  const supportedCodeAction = Boolean(
    oobCode && (mode === 'verifyEmail' || mode === 'resetPassword')
  );

  return {
    active: supportedCodeAction || (onAuthActionPath && hasActionParams),
    mode,
    oobCode,
  };
}
